import { useMemo } from 'react';
import type { SessionSummary } from '../types/session';

export type AnalyticsEntry = {
  themes: string[];
  sentiment: 'Positive' | 'Negative' | 'Neutral';
};

export type TakeawayAnalytics = Record<string, AnalyticsEntry>;

// Very light-weight heuristic analytics based on mom_data text
export function useTakeawayAnalytics(sessions: SessionSummary[] | undefined): TakeawayAnalytics {
  return useMemo(() => {
    const result: TakeawayAnalytics = {};
    if (!sessions || sessions.length === 0) return result;

    const POSITIVE = ['great', 'good', 'improve', 'progress', 'success', 'growth', 'positive'];
    const NEGATIVE = ['issue', 'problem', 'concern', 'risk', 'blocked', 'negative'];

    const THEME_MAP: Record<string, string> = {
      strategy: 'Strategy',
      leadership: 'Leadership',
      communication: 'Communication',
      conflict: 'Conflict',
      productivity: 'Productivity',
      motivation: 'Motivation',
      focus: 'Focus',
      delivery: 'Delivery',
      stakeholder: 'Stakeholder Management',
      career: 'Career',
    };

    for (const s of sessions) {
      const text = JSON.stringify(s.mom_data || {}).toLowerCase();

      // Sentiment
      let score = 0;
      for (const w of POSITIVE) if (text.includes(w)) score += 1;
      for (const w of NEGATIVE) if (text.includes(w)) score -= 1;
      const sentiment: AnalyticsEntry['sentiment'] = score > 0 ? 'Positive' : score < 0 ? 'Negative' : 'Neutral';

      // Themes
      const themes: string[] = [];
      for (const key of Object.keys(THEME_MAP)) {
        if (text.includes(key)) themes.push(THEME_MAP[key]);
      }
      if (themes.length === 0) themes.push('General');

      result[s.id] = { themes, sentiment };
    }

    return result;
  }, [sessions]);
}
